import type { EnrollmentItem } from '@shared/flow/enrollment';
import { contactDisplayIdentity } from '../../EnrollQueue/contactIdentity';
import { RECENT_SEND_BLOCK_LABEL, SEND_SKIP_REASON_LABELS } from '../../EnrollQueue/itemBlockReason';

/**
 * U2 (V2 Glaze rail, plan D16/D17/D36) — small, Glaze-free display helpers
 * for the Plays pane. This file imports ONLY from `EnrollQueue/*` leaf
 * modules (D11) so it stays loadable with no Glaze/DOM dependency at all —
 * pure functions over `EnrollmentItem`.
 *
 * Deliberately NOT ported from the clayground prototype's own
 * `leadDisplay.ts` (U2 Approach note: "Terra writes its own small
 * `leadDisplay.ts` with different contents") — the prototype file has no
 * access to Terra's real `EnrollmentItem` shape or `itemBlockReason.ts`
 * label vocabulary.
 */

/**
 * Local mirror of `@clay-run/glaze/status-badge`'s `StatusBadgeStatus`
 * union. Declared here rather than imported from Glaze: this file is a
 * Glaze-free leaf by design (see the module doc above and D11), and even a
 * type-only import pulls in `@clay-run/glaze/status-badge`'s module graph
 * for anything downstream that isn't using `import type` consistently.
 * Keep this in sync with `StatusBadgeStatus` in
 * `node_modules/@clay-run/glaze/dist/status-badge.d.ts` if Glaze ever adds
 * or renames a status.
 */
export type StatusBadgeStatus = 'neutral' | 'action' | 'success' | 'warning' | 'error';

export interface BlockPill {
  text: string;
  status: StatusBadgeStatus;
  tooltip: string;
}

/**
 * D17 rows that render NO pill at all: `Assign a play first` (the
 * unassigned row already shows the play picker) and the read-only label
 * (rendered once, rail-level, not per row).
 */
const NO_PILL_LABELS = new Set<string>([
  SEND_SKIP_REASON_LABELS.no_play,
  // itemBlockReason.ts's readOnly branch returns this string as an inline
  // literal — it has no exported constant of its own (unlike the other
  // labels below), so it is reproduced verbatim here rather than imported.
  'Enroll queue is read-only while impersonating',
]);

/**
 * D17's label -> { text, status } table, built from `itemBlockReason.ts`'s
 * exported label constants wherever one exists (never retyped). `Queued…`
 * is the one exception: `itemBlockReason.ts` returns it as an inline
 * literal with no exported constant (unlike `SEND_SKIP_REASON_LABELS` and
 * `RECENT_SEND_BLOCK_LABEL`), so it is reproduced verbatim here.
 */
const PILL_BY_LABEL: Record<string, { text: string; status: StatusBadgeStatus }> = {
  'Queued…': { text: 'Queued', status: 'neutral' },
  [SEND_SKIP_REASON_LABELS.sending]: { text: 'Sending…', status: 'neutral' },
  [SEND_SKIP_REASON_LABELS.already_sent]: { text: 'Sent', status: 'neutral' },
  [SEND_SKIP_REASON_LABELS.no_sender]: { text: 'No sender', status: 'error' },
  [SEND_SKIP_REASON_LABELS.no_email]: { text: 'No email', status: 'error' },
  [SEND_SKIP_REASON_LABELS.no_connector]: { text: 'No connector', status: 'error' },
  [RECENT_SEND_BLOCK_LABEL]: { text: 'Recent send', status: 'warning' },
};

/**
 * D17 — exactly one block pill per row, derived from `deriveGroupSendability`
 * / `itemBlockReason`'s label string (server `sendSkipLabelFor` label wins).
 * `null` in, `null` out (nothing to show). An unrecognized label (any label
 * outside the D17 table, including a future server-added `SendSkip` reason)
 * renders as-is: `{ text: label, status: 'neutral', tooltip: label }` — the
 * D17 "any other -> the full string, neutral" fallback row.
 */
export function blockPillFor(label: string | null): BlockPill | null {
  if (label === null) return null;
  if (NO_PILL_LABELS.has(label)) return null;
  const known = PILL_BY_LABEL[label];
  if (known) return { text: known.text, status: known.status, tooltip: label };
  return { text: label, status: 'neutral', tooltip: label };
}

/**
 * D36 — the Plays-pane filter predicate. `q` is ALREADY trimmed and
 * lowercased by the caller (`PlaysPane`'s `query.trim().toLowerCase()`); an
 * empty `q` matches everything (no filter applied yet). Otherwise a
 * case-insensitive `includes` over the contact's display identity, account
 * name, contact email, and the resolved play name — never `contact_title`
 * alone (that field is meta-only; D36 does not list it as a match source).
 *
 * Deliberately NOT the omnibox's matcher (`queueSearchMatcher.ts`'s row
 * matcher) — that one has a 4-row cap, a minimum-query-length gate, and is
 * coupled to the omnibox surface, none of which apply to this client-side,
 * uncapped filter (D13/D36). (Named indirectly here on purpose: this
 * `shell-next/rail` leaf must never literally spell out that matcher's
 * export name or the omnibox surface's component name — see D13/D36's own
 * "never import" list and this unit's verification grep.)
 */
export function matchesQueueFilter(item: EnrollmentItem, playName: string, q: string): boolean {
  if (q === '') return true;
  const haystacks = [
    contactDisplayIdentity(item),
    item.account_name ?? '',
    item.contact_email ?? '',
    playName,
  ];
  return haystacks.some(haystack => haystack.toLowerCase().includes(q));
}

/**
 * D16 — a row's meta line: `contact_title` and `account_name`, `filter(
 * Boolean)`ed (so a null half never leaves a dangling `· `) and joined with
 * ` · `; By Contact additionally folds in the play name as a third part, so
 * `rowMeta(item, playName)` reads `Title · Account · Play` when all three
 * are present and degrades gracefully (no stray separator) when `contact_title`
 * and/or `account_name` are null — e.g. just `Play` for a contact with
 * neither. `playName` omitted (the By Play view, where the play is already
 * the section header) reproduces D16's base two-part meta exactly.
 */
export function rowMeta(item: Pick<EnrollmentItem, 'contact_title' | 'account_name'>, playName?: string): string {
  return [item.contact_title, item.account_name, playName].filter(Boolean).join(' · ');
}
